import { isRefreshEndpointPath, refreshPortalSession } from '@/lib/auth/session-refresh'
import { clearPortalSession, notifyPortalSessionExpired } from '@/lib/auth/portal-session'
import type { PortalKind } from '@/lib/auth/token-storage'

export interface ApiClientOptions {
  /**
   * API 根路径，可以是绝对 URL，也可以是带 `/v1/api` 的相对前缀。
   */
  baseUrl: string
  /**
   * 每次发请求时即时读取 token，避免登录、刷新或登出后的闭包旧值。
   */
  getToken?: () => string | null
  /**
   * 传入门户类型后，请求层会在 401 时尝试对应门户的 refresh token 流程。
   */
  portal?: PortalKind
}

export interface ApiErrorDiagnostics {
  providerMessage?: string
}

export interface BlobResponse {
  blob: Blob
  headers: Headers
}

/**
 * API 错误的统一异常形态。
 * `body` 保留原始响应用于 feature 层兼容旧字段，UI 层仍应优先映射到 i18n 文案。
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
    public readonly errorCode?: string,
    public readonly requestId?: string,
    public readonly errorSource?: string,
    public readonly errorLabel?: string,
    public readonly diagnostics?: ApiErrorDiagnostics,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export function isRefreshablePortalAuthError(error: ApiError) {
  if (error.status !== 401) {
    return false
  }

  // auth.wecom.* 的 401 一律来自 transaction cookie 解析，与 Bearer token 无关；
  // 触发 refresh 会在 refresh 失败时误清有效 session。可刷新的是 auth.token_invalid 这类。
  if (error.errorCode?.startsWith('auth.wecom.') || isChannelReauthenticationError(error)) {
    return false
  }

  return !error.errorCode || error.errorCode.startsWith('auth.')
}

function isChannelReauthenticationError(error: ApiError) {
  return (
    error.status === 401 &&
    (error.errorCode === 'auth.channel_reauthentication_required' ||
      error.errorCode === 'auth.session_source_invalid')
  )
}

export function handleApplicationChannelReauthentication(error: ApiError, portal: PortalKind) {
  if (portal !== 'user' || !isChannelReauthenticationError(error)) return false
  clearPortalSession(portal)
  notifyPortalSessionExpired(portal)
  return true
}

interface ApiErrorBody {
  data?: unknown
  error_code?: unknown
  error_label?: unknown
  error_source?: unknown
  message?: unknown
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function readText(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

function readApiErrorMessage(body: unknown) {
  if (!isRecord(body)) {
    return undefined
  }

  const errorBody = body as ApiErrorBody
  // 旧接口有时把可读错误放在 `data` 字符串，新接口优先使用 `message`。
  return readText(errorBody.message) ?? readText(errorBody.data)
}

function readApiErrorCode(body: unknown) {
  if (!isRecord(body)) {
    return undefined
  }

  const errorBody = body as ApiErrorBody
  return readText(errorBody.error_code)
}

function readApiErrorSource(body: unknown) {
  if (!isRecord(body)) {
    return undefined
  }

  return readText((body as ApiErrorBody).error_source)
}

function readApiErrorLabel(body: unknown) {
  if (!isRecord(body)) {
    return undefined
  }

  return readText((body as ApiErrorBody).error_label)
}

function readApiErrorDiagnostics(body: unknown): ApiErrorDiagnostics | undefined {
  if (!isRecord(body) || !isRecord(body.data) || !isRecord(body.data.diagnostics)) {
    return undefined
  }

  const diagnostics = body.data.diagnostics
  const providerMessage =
    readText(diagnostics.provider_message) ?? readText(diagnostics.providerMessage)
  const normalized = {
    providerMessage,
  }

  return normalized.providerMessage ? normalized : undefined
}

export async function readApiErrorResponse(response: Response) {
  const body = (await response.json().catch(() => undefined)) as unknown
  const message = readApiErrorMessage(body) ?? (response.statusText || 'Request failed')
  const requestId = readText(response.headers.get('x-request-id'))

  return new ApiError(
    message,
    response.status,
    body,
    readApiErrorCode(body),
    requestId,
    readApiErrorSource(body),
    readApiErrorLabel(body),
    readApiErrorDiagnostics(body),
  )
}

/**
 * 创建新前端使用的轻量 fetch 封装。
 * 它负责 API 前缀拼接、JSON 默认请求头、multipart boundary 避让、Bearer token 注入和门户 401 自动刷新；
 * 具体 DTO 解析、错误文案映射和业务状态仍留在 feature adapter。
 */
export function createApiClient({ baseUrl, getToken, portal }: ApiClientOptions) {
  const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl

  function resolveUrl(path: string) {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`

    if (normalizedBaseUrl.startsWith('http')) {
      return `${normalizedBaseUrl}${normalizedPath}`
    }

    // `new URL('/x', origin)` 会丢掉 `/v1/api` 这类相对 API 前缀。
    return new URL(`${normalizedBaseUrl}${normalizedPath}`, window.location.origin).toString()
  }

  function buildRequestInit(init: RequestInit = {}) {
    const token = getToken?.()
    const headers = new Headers(init.headers)
    const hasFormDataBody = typeof FormData !== 'undefined' && init.body instanceof FormData

    if (!headers.has('content-type') && !hasFormDataBody) {
      headers.set('content-type', 'application/json')
    }

    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }

    return {
      ...init,
      headers,
    }
  }

  async function requestWithParser<T>(
    path: string,
    init: RequestInit,
    parseSuccess: (response: Response) => Promise<T>,
  ): Promise<T> {
    const response = await fetch(resolveUrl(path), {
      ...buildRequestInit(init),
    })

    if (response.status === 401 && portal && !isRefreshEndpointPath(path)) {
      const error = await readApiErrorResponse(response)

      if (handleApplicationChannelReauthentication(error, portal)) {
        throw error
      }

      if (isRefreshablePortalAuthError(error)) {
        // refresh 成功后重放一次原请求；失败时由 session refresh 触发统一过期事件。
        const refreshed = await refreshPortalSession(portal, resolveUrl)
        if (refreshed) {
          const retryResponse = await fetch(resolveUrl(path), {
            ...buildRequestInit(init),
          })

          if (retryResponse.ok) {
            return await parseSuccess(retryResponse)
          }

          const retryError = await readApiErrorResponse(retryResponse)
          handleApplicationChannelReauthentication(retryError, portal)
          throw retryError
        }
      }

      throw error
    }

    if (!response.ok) {
      // 后端非 2xx 仍会返回 API 错误外壳，不能退化成笼统的网络错误。
      throw await readApiErrorResponse(response)
    }

    return await parseSuccess(response)
  }

  function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    // 不额外包一层 async，保持与拆出 requestWithParser 之前相同的 promise 解析层数。
    return requestWithParser(path, init, (response) => response.json() as Promise<T>)
  }

  /**
   * 请求二进制响应并保留响应头，供下载文件名等元数据解析使用。
   * 非 2xx 响应仍按统一 JSON 错误外壳解析为 `ApiError`，401 也沿用门户 refresh 重放流程。
   */
  async function requestBlob(path: string, init: RequestInit = {}): Promise<BlobResponse> {
    return await requestWithParser(path, init, async (response) => ({
      blob: await response.blob(),
      headers: response.headers,
    }))
  }

  return {
    request,
    requestBlob,
  }
}
